import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { getInformationReturnPath } from "@kozien/shared";
import { SunProtectionHeader } from "@/components/organisms/SunProtectionHeader/SunProtectionHeader";
import { StepIndicator } from "@/components/molecules/StepIndicator/StepIndicator";
import { BackToMeasurementForm } from "@/components/molecules/BackToMeasurementForm/BackToMeasurementForm";
import { DiscountBanner } from "@/components/organisms/DiscountBanner/DiscountBanner";
import { BottomBar } from "@/components/organisms/BottomBar/BottomBar";
import { AddOtherCategoryDialog } from "@/components/molecules/AddOtherCategoryDialog/AddOtherCategoryDialog";
import { useSessionStore } from "@/store/session";
import { useStepsStore } from "@/store/steps";
import { getAllConfiguredCombos, useSelectionStore } from "@/store/selection";
import { useOrderStore } from "@/store/order";
import { useDealProducts } from "@/models/deal";
import {
    getSunProtectionOrderRole,
    mapCombosToProductInputs,
    useRestoreOrder,
} from "@/models/order";
import { useContact } from "@/models/contact";
import { useDiscountInfo } from "@/models/discount";
import { useSunProtectionPageContent } from "@/models/sun-protection-settings";
import { type ProductType, useProducts } from "@/models/product";
import { CategoryStep } from "./steps/CategoryStep";
import { TypeStep } from "./steps/TypeStep";
import { WindowsStep } from "./steps/WindowsStep";
import { ColorStep } from "./steps/ColorStep";
import { ConfirmStep } from "./steps/ConfirmStep";
import { MeasurementDonePage } from "./MeasurementDonePage";
import { getSunProtectionPageGate } from "./getSunProtectionPageGate";

const STEP_COMPONENTS = [
    CategoryStep,
    TypeStep,
    WindowsStep,
    ColorStep,
    ConfirmStep,
];

export function SunProtectionPage() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const init = useSessionStore((s) => s.init);
    const dealId = useSessionStore((s) => s.dealId);
    const discountCode = useSessionStore((s) => s.discountCode);
    const backendDealId = useSessionStore((s) => s.backendDealId);
    const { steps, currentStepIndex, setSteps, goTo } = useStepsStore();
    const hasRedirectedToOverview = useRef(false);
    const hasRedirectedToThankYou = useRef(false);
    const [missingCategory, setMissingCategory] = useState<ProductType | null>(
        null,
    );
    const selectedCategory = useSelectionStore((s) => s.selectedCategory);
    const selectedTypeId = useSelectionStore((s) => s.selectedTypeId);
    const selectedProductIds = useSelectionStore((s) => s.selectedProductIds);
    const windowSelections = useSelectionStore((s) => s.windowSelections);
    const submit = useOrderStore((s) => s.submit);
    const isSubmitting = useOrderStore((s) => s.isSubmitting);
    const isSuccess = useOrderStore((s) => s.isSuccess);
    const hasPreviousOrder = useOrderStore((s) => s.hasPreviousOrder);
    const signatureUrl = useOrderStore((s) => s.signatureUrl);
    const isMeasurementSigned = useOrderStore((s) => s.isMeasurementSigned);
    const orderId = useOrderStore((s) => s.orderId);
    const { products } = useProducts(selectedCategory);
    const selectedProduct =
        products.find((p) => p.id === selectedTypeId) ?? null;
    useDealProducts();
    const { loading: isRestoringOrder } = useRestoreOrder();
    useContact();
    const discountInfo = useDiscountInfo();
    const { data: pageSettings } = useSunProtectionPageContent();

    const role = getSunProtectionOrderRole(searchParams.get("returnKey"));
    const gate = getSunProtectionPageGate({
        role,
        hasSignature: Boolean(signatureUrl),
        isMeasurementSigned,
    });
    const hasPlacedOrder = gate === "placedOrder";
    const showMeasurementDone = gate === "measurementSigned";
    // Initialise steps and session from URL params (mount-only).
    // If the page is opened without a `step` query, treat it as a fresh
    // entry: clear any persisted Zustand selection so we don't show stale
    // local-only selections. `useRestoreOrder` repopulates from the backend.
    useEffect(() => {
        setSteps([
            { id: "category", label: t("steps.category.label") },
            { id: "type", label: t("steps.type.label") },
            { id: "windows", label: t("steps.windows.label") },
            { id: "color", label: t("steps.color.label") },
            { id: "confirm", label: t("steps.confirm.label") },
        ]);

        init({
            dealId: searchParams.get("dealId") ?? "",
            email: searchParams.get("email") ?? "",
            discountCode: searchParams.get("discountCode") ?? "",
        });

        // Clear order status from any previously-viewed deal so the gate is
        // never evaluated against stale state before `useRestoreOrder`
        // repopulates it for this deal.
        const orderStore = useOrderStore.getState();
        orderStore.setSignatureUrl("");
        orderStore.setIsMeasurementSigned(false);
        orderStore.setHasPreviousOrder(false);
        orderStore.setOrderId("");

        if (!searchParams.has("step")) {
            useSelectionStore.getState().reset();
        }
        // Mount-only — searchParams snapshot is intentional (we read URL
        // params once on first render; subsequent URL changes don't re-init).
    }, [init, t]);

    // Override step labels from CMS once loaded
    useEffect(() => {
        if (!pageSettings) return;
        setSteps([
            {
                id: "category",
                label:
                    pageSettings.decorationTypeStep?.stepName ??
                    t("steps.category.label"),
            },
            {
                id: "type",
                label:
                    pageSettings.windowDressingStep?.stepName ??
                    t("steps.type.label"),
            },
            {
                id: "windows",
                label:
                    pageSettings.productSelectionStep?.stepName ??
                    t("steps.windows.label"),
            },
            {
                id: "color",
                label:
                    pageSettings.colorsStep?.stepName ?? t("steps.color.label"),
            },
            {
                id: "confirm",
                label:
                    pageSettings.overviewStep?.stepName ??
                    t("steps.confirm.label"),
            },
        ]);
    }, [pageSettings, setSteps, t]);

    // Sync step index from URL so browser back/forward works
    useEffect(() => {
        if (isSuccess) return;
        const stepParam = searchParams.get("step");
        const index = stepParam !== null ? parseInt(stepParam, 10) : 0;
        goTo(isNaN(index) ? 0 : index);
    }, [searchParams, goTo, isSuccess]);

    // Navigate directly to overview when a previous order is detected —
    // any deal that already has products configured jumps to step 4 on load.
    // Only fires when no explicit `step` is set in the URL, so a deep link
    // like `?step=1` always wins.
    const hasStepParam = searchParams.has("step");
    useEffect(() => {
        // A blocked/redirected customer must not be sent into the overview.
        if (hasPlacedOrder || showMeasurementDone) return;
        if (
            !hasPreviousOrder ||
            steps.length === 0 ||
            hasRedirectedToOverview.current ||
            hasStepParam
        )
            return;
        hasRedirectedToOverview.current = true;
        setSearchParams(
            (prev) => {
                prev.set("step", String(steps.length - 1));
                return prev;
            },
            { replace: true },
        );
    }, [
        hasPreviousOrder,
        steps.length,
        setSearchParams,
        hasStepParam,
        hasPlacedOrder,
        showMeasurementDone,
    ]);

    // A customer who already placed an order is sent straight to the order
    // confirmation page, which shows the "Bedankt voor je bestelling" success
    // state and offers no way back into the flow (THER-2244).
    useEffect(() => {
        if (!hasPlacedOrder || hasRedirectedToThankYou.current) return;
        if (!orderId) return;
        hasRedirectedToThankYou.current = true;
        const params = new URLSearchParams();
        params.set("sunProtectionOrderId", orderId);
        if (dealId) params.set("dealId", dealId);
        navigate(`/zonwering/bestelling-bevestigen?${params.toString()}`, {
            replace: true,
        });
    }, [hasPlacedOrder, dealId, orderId, navigate]);

    const isMeasurer = searchParams.get("returnKey") === "measurement";

    async function submitOrder() {
        if (backendDealId == null) return;
        const combos = getAllConfiguredCombos(useSelectionStore.getState());
        // Measurer may submit an empty order (no products yet).
        if (combos.length === 0 && !isMeasurer) return;
        const productInputs = mapCombosToProductInputs(combos);
        const result = await submit({
            dealId: backendDealId,
            productInputs,
            discountCode: discountCode || undefined,
            role,
        });
        if (!result?.orderId) return;
        // Came from the portal measurement form — go straight back there
        // instead of showing the order-placed page.
        if (isMeasurer) {
            const measurementUrl = getInformationReturnPath({
                returnKey: "measurement",
                email: searchParams.get("email") ?? "",
                dealId: searchParams.get("dealId") ?? "",
                quoteId: searchParams.get("quoteId") ?? undefined,
            });
            if (measurementUrl) {
                window.location.href = `${measurementUrl}#sun-protection-overview`;
                return;
            }
        }
        const params = new URLSearchParams();
        if (dealId) params.set("dealId", dealId);
        params.set("sunProtectionOrderId", result.orderId);
        navigate(`/zonwering/bedankt?${params.toString()}`, {
            replace: true,
        });
    }

    async function handleNext() {
        if (currentStepIndex >= steps.length - 1) {
            // Measurer can finish at any time, even with no products.
            if (isMeasurer) {
                await submitOrder();
                return;
            }
            const combos = getAllConfiguredCombos(useSelectionStore.getState());
            if (combos.length === 0) return;
            const hasSunProtection = combos.some(
                (c) => c.category === "SUN_PROTECTION",
            );
            const hasWindowDecoration = combos.some(
                (c) => c.category === "WINDOW_DECORATION",
            );
            if (hasSunProtection && hasWindowDecoration) {
                await submitOrder();
                return;
            }
            setMissingCategory(
                hasSunProtection ? "WINDOW_DECORATION" : "SUN_PROTECTION",
            );
            return;
        }
        setSearchParams(
            (prev) => {
                prev.set("step", String(currentStepIndex + 1));
                return prev;
            },
            { replace: false },
        );
    }

    function handleDialogAddMore() {
        const category = missingCategory;
        setMissingCategory(null);
        if (!category) return;
        useSelectionStore.getState().selectCategory(category);
        setSearchParams(
            (prev) => {
                prev.set("step", "0");
                return prev;
            },
            { replace: false },
        );
    }

    async function handleDialogConfirm() {
        setMissingCategory(null);
        await submitOrder();
    }

    function handleBack() {
        if (currentStepIndex > 0) {
            setSearchParams(
                (prev) => {
                    prev.set("step", String(currentStepIndex - 1));
                    return prev;
                },
                { replace: false },
            );
        } else {
            navigate(-1);
        }
    }

    const StepContent = STEP_COMPONENTS[currentStepIndex] ?? TypeStep;
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === steps.length - 1;

    // Step validation — add cases here as new steps gain required fields
    const allWindowsConfigured =
        !!selectedProduct &&
        selectedProductIds.length > 0 &&
        selectedProductIds.every((id) => {
            const sel = windowSelections[id];
            if (!sel) return false;
            if (selectedProduct.hasFrameColor && !sel.frameColorId)
                return false;
            return true;
        });

    const isNextDisabled =
        {
            0: selectedCategory === null,
            1: selectedTypeId === null,
            2: selectedProductIds.length === 0,
            3: !allWindowsConfigured,
            4: isSubmitting,
        }[currentStepIndex] ?? false;

    const statusText =
        currentStepIndex === 2
            ? t("bottomBar.windowsSelected", {
                  count: selectedProductIds.length,
              })
            : undefined;

    const measurementFormUrl = getInformationReturnPath({
        returnKey: "measurement",
        email: searchParams.get("email") ?? "",
        dealId: searchParams.get("dealId") ?? "",
        quoteId: searchParams.get("quoteId") ?? undefined,
    });

    const loadingScreen = (
        <div className="min-h-screen bg-gray-50">
            <SunProtectionHeader />
            <main className="mx-auto max-w-6xl px-4 py-12">
                <p className="text-sm text-gray-500">{t("loading")}</p>
            </main>
        </div>
    );

    if (isRestoringOrder) return loadingScreen;

    // Blocked: measurement signed, no order placed.
    if (showMeasurementDone) {
        return <MeasurementDonePage phoneNumber={pageSettings?.phoneNumber} />;
    }

    if (hasPlacedOrder) {
        if (!orderId) {
            return (
                <MeasurementDonePage phoneNumber={pageSettings?.phoneNumber} />
            );
        }
        return loadingScreen;
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <SunProtectionHeader />

            {measurementFormUrl && (
                <div className="bg-white px-4 pt-3 pb-2 border-b border-gray-100">
                    <div className="max-w-6xl mx-auto">
                        <BackToMeasurementForm
                            href={measurementFormUrl}
                            label={t("backToMeasurementForm")}
                        />
                    </div>
                </div>
            )}

            <div className="flex-1 flex flex-col">
                <div className="bg-white py-5 border-b border-gray-100">
                    <StepIndicator
                        steps={steps}
                        currentStepIndex={currentStepIndex}
                        ariaLabel={t("steps.ariaLabel")}
                    />
                </div>

                {discountCode && discountInfo && !isSuccess && (
                    <div className="pt-5 flex justify-center px-4">
                        <DiscountBanner
                            discountCode={discountCode}
                            title={discountInfo.title}
                            description={discountInfo.description}
                        />
                    </div>
                )}

                <main className="flex-1 w-full max-w-6xl mx-auto px-4 py-6 pb-28">
                    <StepContent />
                </main>
            </div>

            <BottomBar
                onNext={handleNext}
                onBack={handleBack}
                isFirstStep={isFirstStep}
                isLastStep={isLastStep}
                nextDisabled={isNextDisabled}
                statusText={statusText}
                nextButtonText={pageSettings?.nextButtonText}
                finishButtonText={pageSettings?.finishOrderButtonText}
                phoneNumber={pageSettings?.phoneNumber}
            />

            <AddOtherCategoryDialog
                open={missingCategory !== null}
                missingCategory={missingCategory ?? "SUN_PROTECTION"}
                onAddMore={handleDialogAddMore}
                onConfirm={handleDialogConfirm}
                onClose={() => setMissingCategory(null)}
            />
        </div>
    );
}
